import { FC, useState, useEffect, useRef, ChangeEvent } from "react";
import firebase from 'firebase/compat/app';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import ListGroup from 'react-bootstrap/ListGroup';
import Container from 'react-bootstrap/Container';
import Stack from 'react-bootstrap/Stack';
import TemplateDataService from "../services/template.service";
import ITemplateData from "../types/template.type";
import FontDataService from "../services/font.service";
import IFontData, { IFile } from "../types/font.type";
import _ from 'lodash';
import pretty from 'pretty';
import { authFirebase } from "../firebase";
import { styleManager } from "../configs/styleManager";
import { plugins, pluginsOpts } from "../configs/plugins";
import { removeHtmlMetaTag, removeCssMedia, removeCssFontFace, cleanCss, getFormatFont, formatDateGMTZero, isValidFile } from '../utils';
import { UploadFile, UPLOAD_TYPE, EXT_IMG_FILE } from '../storageAman';
import ToastMessage from "./toast.component";
import FontModal from "./font-modal.component";
import { ToastRef, FontModalRef } from "./interfaces";

import grapesJS from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';

interface IProps { };

const DASHBOARD_HOST = process.env.REACT_APP_DASHBOARD_HOST;
const DASHBOARD_OLD_HOST = process.env.REACT_APP_DASHBOARD_OLD_HOST;

const ECardTemplate: FC<IProps> = () => {

  const unsubscribeRef = useRef<any>(null);
  const toastRef = useRef<ToastRef>(null);
  const fontModalRef = useRef<FontModalRef>(null);

  const [editorRoot, setEditorRoot] = useState<grapesJS.Editor | null>(null);
  const [contentHtml, setContentHtml] = useState<string | null>(null);
  const [templates, setTemplates] = useState<ITemplateData[]>([]);
  const [newFonts, setNewFonts] = useState<IFontData[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<ITemplateData>({
    id: null,
    title: "",
    content: "",
    html: "",
    css: "",
    type: "ECARD",
    createdTime: new Date(),
  });
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [lang, setLang] = useState<string>("");
  const [isAuthFirebase, setIsAuthFirebase] = useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(false);
  const [srcs, setSrcs] = useState<string[]>([]);

  useEffect(() => {
    setEditorRoot(grapesJS.init({
      container: '#gjs',
      width: 'auto',
      height: 'calc(100vh - 60px)',
      avoidInlineStyle: true,
      assetManager: {
        uploadFile: uploadFileStorage
      },
      storageManager: false,
      styleManager,
      plugins,
      pluginsOpts,
    }));
    if (unsubscribeRef.current) unsubscribeRef.current();
    window.addEventListener("message", receiveDataFromWeb, false);
    return () => {
      window.removeEventListener("message", receiveDataFromWeb, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (srcs.length > 0) {
      const assetManager = editorRoot?.AssetManager;
      assetManager?.add(srcs);
      assetManager?.render([...assetManager?.getAll()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcs]);

  useEffect(() => {
    if (editorRoot && contentHtml) {
      const fetchData = async () => {
        const fonts = await getAllFonts();
        setNewFonts(fonts);
        addFontEditor(fonts);
        editorRoot?.setComponents(removeHtmlMetaTag(contentHtml), { avoidUpdateStyle: true });
      }
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRoot, contentHtml]);

  const receiveDataFromWeb = (data: any) => {
    if (data && (data.origin === DASHBOARD_HOST || data.origin === DASHBOARD_OLD_HOST) && data.data && data.data.type === 'SEND_TO_IFRAME') {
      const {
        // i18nextLng,
        accessToken,
        defaultRole,
        firebaseToken,
        lang,
        title,
        contentHtml,
      } = data.data;
      // localStorage.setItem('i18nextLng', i18nextLng);
      localStorage.setItem('access-token', accessToken);
      localStorage.setItem('default-role', defaultRole);
      setCurrentTemplate({ ...currentTemplate, title: title });
      setLang(lang);
      authFirebase(firebaseToken, async (user: firebase.User) => {
        setIsAuthFirebase(user ? true : false);
        if (user) {
          unsubscribeRef.current = TemplateDataService.getAll().where("type", "==", "ECARD").orderBy("createdTime", "desc").onSnapshot(onDataChange);
          setContentHtml(contentHtml);
        }
      });
    }
  }

  const uploadFileStorage = (e: any) => {
    const dates: string[] = [];
    const files = Array.from(e.dataTransfer ? e.dataTransfer.files : e.target.files)
      .map((item: any) => {
        const { name, size } = item;
        const currItem = item;
        currItem.fileName = name;
        currItem.fileSize = size;
        let dateNow = formatDateGMTZero(null, "YYYY-MM-DD-HH-mm-ss");
        do {
          dateNow = formatDateGMTZero(null, "YYYY-MM-DD-HH-mm-ss");
        }
        while (dates.indexOf(dateNow) >= 0);
        dates.push(dateNow);
        const fileName = `${dateNow}-${name}`;
        return Object.defineProperty(currItem, 'name', {
          writable: true,
          value: fileName
        });
      });
    if (files.length === 0) {
      return;
    }
    let isCheckFile = true;
    files.forEach(file => {
      if (!isValidFile(file, EXT_IMG_FILE)) {
        isCheckFile = false;
      }
    });
    if (!isCheckFile) {
      toastRef.current?.showMessage({
        variant: 'warning',
        content: 'Invalid file upload !'
      });
      return;
    }
    UploadFile(files[0], UPLOAD_TYPE.ECARDS, null, () => { }, (result) => {
      setSrcs([...srcs, result.MediaLink]);
    }, () => {
      toastRef.current?.showMessage({
        variant: 'danger',
        content: 'Upload images unsuccessfully !'
      });
    });
  };

  const addFontEditor = (fonts: IFontData[]) => {
    let styleManager = editorRoot?.StyleManager;
    let fontProperty = styleManager?.removeProperty('typography', 'font-family');
    if (fontProperty && fontProperty.attributes && fontProperty.attributes.options) {
      const allFonts = [...fontProperty.attributes.options];
      fonts.forEach(item => {
        allFonts.push({ id: item.fontFamily, label: item.fontFamily });
      });
      fontProperty.attributes.options = _.orderBy(_.uniqBy(allFonts, 'id'), ['label'], ['asc']);
    }
    styleManager?.addProperty('typography', fontProperty, { at: 0 });
  }

  const getAllFonts = async (): Promise<IFontData[]> => {
    const snapshot = await FontDataService.getAll().orderBy("fontFamily", "asc").get();
    const data: Array<any> = [];
    snapshot.docs.forEach((item: any) => {
      data.push({
        id: item.id,
        ...item.data(),
      });
    });
    return data as IFontData[];
  }

  const handleChangeFonts = (items: IFontData[]) => {
    setNewFonts(items);
    addFontEditor(items);
  }

  const onDataChange = (items: any) => {
    let array = new Array<ITemplateData>();
    items.forEach((item: any) => {
      let id = item.id;
      let data = item.data();
      array.push({
        id: id,
        title: data.title,
        content: data.content,
        html: data.html,
        css: data.css,
        type: data.type,
        createdTime: data.createdTime,
      });
    });
    setTemplates(array);
  }

  const refreshList = () => {
    setCurrentTemplate({
      id: null,
      title: "",
      content: "",
      html: "",
      css: "",
      type: "ECARD",
      createdTime: new Date(),
    });
    setCurrentIndex(-1);
  }

  const setActiveTemplate = (item: ITemplateData, index: number) => {
    editorRoot?.Components.clear();
    editorRoot?.DomComponents.clear();
    editorRoot?.CssComposer.clear();
    editorRoot?.UndoManager.clear();
    editorRoot?.setComponents(removeHtmlMetaTag(item.content), { avoidUpdateStyle: true });
    setCurrentTemplate(item);
    setCurrentIndex(index);
  }

  const onChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    setCurrentTemplate({ ...currentTemplate, title });
  }

  const submitForm = (event: any) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      event.preventDefault();
      event.stopPropagation();
      saveTemplate();
    }
    setValidated(true);
  }

  const saveTemplate = (callback?: Function) => {
    if (editorRoot && currentTemplate.title) {
      let data: any;
      for (const temp of templates) {
        if (temp.title === currentTemplate.title) {
          data = temp;
        }
      }
      const { html, css, fullHtml } = getFullHtml(editorRoot);
      // Update
      if (data && data.id) {
        data.content = fullHtml;
        data.html = html;
        data.css = css;
        data.type = currentTemplate.type;
        data.createdTime = new Date();
        TemplateDataService.update(data.id, data)
          .then(() => {
            toastRef.current?.showMessage({
              variant: 'success',
              content: 'Update template successfully !'
            });
            if (callback) callback(fullHtml);
          })
          .catch((e: Error) => {
            toastRef.current?.showMessage({
              variant: 'danger',
              content: 'Update template unsuccessfully !'
            });
            console.log(e);
          });
      } else {
        // Create
        data = {
          title: currentTemplate.title,
          content: fullHtml,
          html,
          css,
          type: currentTemplate.type,
          createdTime: new Date(),
        };
        TemplateDataService.create(data)
          .then(() => {
            toastRef.current?.showMessage({
              variant: 'success',
              content: 'Created new template successfully !'
            });
            if (callback) callback(fullHtml);
          })
          .catch((e: Error) => {
            toastRef.current?.showMessage({
              variant: 'danger',
              content: 'Created new template unsuccessfully !'
            });
            console.log(e);
          });
      }
    } else {
      toastRef.current?.showMessage({
        variant: 'danger',
        content: 'An error occurred while save template !'
      });
    }
  }

  const deleteTemplate = (item: any) => {
    if (item && item.id) {
      TemplateDataService.delete(item.id)
        .then(() => {
          toastRef.current?.showMessage({
            variant: 'success',
            content: 'Delete template successfully !'
          });
          refreshList();
        })
        .catch((e: Error) => {
          toastRef.current?.showMessage({
            variant: 'danger',
            content: 'Delete template unsuccessfully !'
          });
          console.log(e);
        });
    }
  }

  const applyTemplate = () => {
    if (editorRoot && currentTemplate.title) {
      saveTemplate((fullHtml: string) => {
        const data = {
          type: 'SEND_FROM_IFRAME',
          event: 'APPLY',
          content: fullHtml,
          lang: lang
        }
        window.parent.postMessage(data, DASHBOARD_HOST || '');
        window.parent.postMessage(data, DASHBOARD_OLD_HOST || '');
      });
    }
  }

  const closeTemplate = () => {
    const data = {
      type: 'SEND_FROM_IFRAME',
      event: 'CLOSE',
      content: null,
      lang: lang
    }
    window.parent.postMessage(data, DASHBOARD_HOST || '');
    window.parent.postMessage(data, DASHBOARD_OLD_HOST || '');
  }

  const getFullHtml = (editor: grapesJS.Editor) => {
    let html: string = "", css: string = "";
    const pagesHtml = editor.Pages.getAll().map((page: any) => {
      const component = page.getMainComponent();
      return {
        html: editor.getHtml({ component, cleanId: true }),
        css: editor.getCss({ component })
      }
    });
    if (pagesHtml && pagesHtml.length > 0) {
      html = pagesHtml[0].html;
      css = removeCssFontFace(removeCssMedia(cleanCss(pagesHtml[0].css)));
    }
    // Set new fonts
    let fontFaces = "";
    const baseFontFace =
      `@font-face {
        font-family: {{FontName}};
        src: {{FontUrl}};
      }
      `;
    newFonts.forEach((item: IFontData) => {
      if (css.includes(item.fontFamily)) {
        let fontFace = baseFontFace.replace("{{FontName}}", item.fontFamily);
        const urls: string[] = [];
        item.urls.forEach((el: IFile) => {
          const formatFont = getFormatFont(el.mediaLink);
          if (formatFont) {
            urls.push(`url('${el.mediaLink}') format('${formatFont}')`);
          } else {
            urls.push(`url('${el.mediaLink}')`);
          }
        });
        fontFace = fontFace.replace("{{FontUrl}}", urls.join(', '));
        fontFaces += fontFace;
      }
    });

    let fullHtml: string = `
      <!doctype html>
      <html lang="en">
        <head>
          <meta charset="utf-8">
          <style>
          ${fontFaces}
          ${css}
          </style>
        </head>
        ${html}
      </html>`;
    return { html, css, fullHtml: pretty(fullHtml) }
  }

  return (
    <>
      <Container fluid>
        <Row>
          <Col xs={3} style={{ paddingLeft: '0px', paddingRight: '0px', textAlign: 'center', alignSelf: 'center' }}>
            <h4>Template E-Card</h4>
          </Col>
          <Col xs={9} style={{ paddingLeft: '0px', paddingRight: '0px' }}>
            <Form noValidate validated={validated} onSubmit={submitForm} style={{ padding: '10px' }}>
              <Stack direction="horizontal" gap={2}>
                <Form.Group>
                  <Form.Control
                    type="text"
                    placeholder="Template name"
                    value={currentTemplate.title}
                    onChange={onChangeTitle}
                    required
                  />
                </Form.Group>
                <Button
                  type="submit"
                  variant="info"
                  disabled={!isAuthFirebase}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={applyTemplate}
                >
                  {isAuthFirebase ? 'Save & Apply' : 'Apply'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="me-auto"
                  onClick={closeTemplate}
                >
                  Close
                </Button>
                <Button
                  type="button"
                  variant="warning"
                  onClick={() => fontModalRef.current?.showModal()}
                >
                  Font Manager
                </Button>
              </Stack>
            </Form>
          </Col>
        </Row>
        <Row className="ecard-list">
          <Col xs={3} style={{ paddingLeft: '0px', paddingRight: '0px', background: '#463a3c' }}>
            <ListGroup as="ul" numbered variant="flush" style={{ height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
              {templates && templates.map((template, index) => (
                <ListGroup.Item
                  key={`key-template-${index + 1}`}
                  as="li"
                  onClick={() => setActiveTemplate(template, index)}
                  className="d-flex justify-content-between align-items-start"
                  style={{
                    cursor: 'pointer',
                    color: (index === currentIndex) ? '#d97aa6' : '#b9a5a6',
                    backgroundColor: '#463a3c',
                  }}
                >
                  <div className="ms-2 me-auto">
                    <div className="fw-bold">{template.title}</div>
                  </div>
                  <Badge
                    bg=""
                    pill
                    style={{ cursor: 'pointer', backgroundColor: '#3c3133' }}
                    onClick={() => deleteTemplate(template)}
                  >
                    <span className="fa fa-close" style={{ color: (index === currentIndex) ? '#d97aa6' : '#b9a5a6' }} aria-hidden="true"></span>
                  </Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
          <Col xs={9} style={{ paddingLeft: '0px', paddingRight: '0px' }}>
            <div id='gjs'></div>
          </Col>
        </Row>
      </Container>
      <FontModal ref={fontModalRef} onChangeFonts={handleChangeFonts} />
      <ToastMessage ref={toastRef} />
    </>
  );
}

export default ECardTemplate;